import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { Home, Building2, Package, Clock } from 'lucide-react';
export function ServicesSection() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].services;
  const icons = [<Home size={40} className="text-mf-yellow" data-id="element-296" />, <Building2 size={40} className="text-mf-yellow" data-id="element-297" />, <Package size={40} className="text-mf-yellow" data-id="element-298" />, <Clock size={40} className="text-mf-yellow" data-id="element-299" />];
  return <section id="services" className="py-24 bg-mf-black text-white relative overflow-hidden" data-id="element-300">
      {/* Modern abstract background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-301">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-mf-red/20 rounded-full blur-[120px]" data-id="element-302"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] bg-mf-yellow/10 rounded-full blur-[120px]" data-id="element-303"></div>
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
      }} data-id="element-304"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-305">
        <div className="text-center mb-20" data-id="element-306">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="text-4xl md:text-6xl font-condensed font-black uppercase tracking-tight mb-6 text-white" data-id="element-307">
            {text.title}
          </motion.h2>
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: 96
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="h-1.5 bg-mf-red mx-auto rounded-full" data-id="element-308"></motion.div>
        </div>

        {/* Modern Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-309">
          {/* Large Item 1 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="md:col-span-2 bg-white/5 backdrop-blur-lg rounded-3xl p-8 md:p-10 border border-white/10 hover:border-mf-red/50 transition-colors duration-500 group relative overflow-hidden" data-id="element-310">
            <div className="absolute top-0 right-0 w-64 h-64 bg-mf-red/10 rounded-full blur-[80px] group-hover:bg-mf-red/20 transition-colors duration-500" data-id="element-311"></div>
            <div className="relative z-10" data-id="element-312">
              <div className="bg-white/10 w-20 h-20 rounded-2xl flex items-center justify-center mb-8 shadow-lg backdrop-blur-md border border-white/5 group-hover:scale-110 transition-transform duration-500" data-id="element-313">
                {icons[0]}
              </div>
              <h3 className="text-3xl md:text-4xl font-condensed font-black uppercase mb-4 text-white tracking-wide" data-id="element-314">
                {text.items[0].title}
              </h3>
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-xl" data-id="element-315">
                {text.items[0].desc}
              </p>
            </div>
          </motion.div>

          {/* Small Item 1 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10 hover:border-mf-yellow/50 transition-colors duration-500 group" data-id="element-316">
            <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg backdrop-blur-md border border-white/5 group-hover:scale-110 transition-transform duration-500" data-id="element-317">
              {icons[1]}
            </div>
            <h3 className="text-2xl font-condensed font-black uppercase mb-4 text-white tracking-wide" data-id="element-318">
              {text.items[1].title}
            </h3>
            <p className="text-gray-300 leading-relaxed" data-id="element-319">
              {text.items[1].desc}
            </p>
          </motion.div>

          {/* Small Item 2 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10 hover:border-mf-yellow/50 transition-colors duration-500 group" data-id="element-320">
            <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg backdrop-blur-md border border-white/5 group-hover:scale-110 transition-transform duration-500" data-id="element-321">
              {icons[2]}
            </div>
            <h3 className="text-2xl font-condensed font-black uppercase mb-4 text-white tracking-wide" data-id="element-322">
              {text.items[2].title}
            </h3>
            <p className="text-gray-300 leading-relaxed" data-id="element-323">
              {text.items[2].desc}
            </p>
          </motion.div>

          {/* Large Item 2 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="md:col-span-2 bg-white/5 backdrop-blur-lg rounded-3xl p-8 md:p-10 border border-white/10 hover:border-mf-red/50 transition-colors duration-500 group relative overflow-hidden" data-id="element-324">
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-mf-red/10 rounded-full blur-[80px] group-hover:bg-mf-red/20 transition-colors duration-500" data-id="element-325"></div>
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-8" data-id="element-326">
              <div className="bg-white/10 w-20 h-20 rounded-2xl flex-shrink-0 flex items-center justify-center shadow-lg backdrop-blur-md border border-white/5 group-hover:scale-110 transition-transform duration-500" data-id="element-327">
                {icons[3]}
              </div>
              <div data-id="element-328">
                <h3 className="text-3xl md:text-4xl font-condensed font-black uppercase mb-4 text-white tracking-wide" data-id="element-329">
                  {text.items[3].title}
                </h3>
                <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-xl" data-id="element-330">
                  {text.items[3].desc}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}